import type { ChangeEvent } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { ImageIcon, RefreshCw } from 'lucide-react';

import {
  Button,
  Card,
  CardContent,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Switch,
} from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import {
  A2vOverlayPosition,
  A2vTemplateType,
  isValidHttpUrl,
  nextAvailableOverlayPosition,
  type A2vConfiguration,
} from '../../model';
import type { NetworkTemplateFormValue } from '../../networkTemplateFormSchema';
import { OverlayPositionPicker } from '../OverlayPositionPicker/OverlayPositionPicker';

interface NetworkTemplateConfigurationPanelProps {
  configuration: A2vConfiguration;
  canSave: boolean;
  pending: boolean;
  qrGenerating: boolean;
  onChooseLogo: () => void;
  onQrValueChange: (value: string) => void;
  onQrEnabledChange: (enabled: boolean) => void;
  onGenerateQr: (value: string) => void;
}

export function NetworkTemplateConfigurationPanel({
  configuration,
  canSave,
  pending,
  qrGenerating,
  onChooseLogo,
  onQrValueChange,
  onQrEnabledChange,
  onGenerateQr,
}: NetworkTemplateConfigurationPanelProps) {
  const form = useFormContext<NetworkTemplateFormValue>();
  const disabled = !canSave || pending;

  return (
    <Card className="overflow-hidden">
      <CardContent className="space-y-0 p-0">
        <section className="space-y-4 px-5 py-4">
          <h2 className="text-base font-semibold">
            {dashboardT('a2v.networkTemplate.configuration')}
          </h2>
          <Controller
            control={form.control}
            name="templateType"
            render={({ field }) => (
              <RadioGroup
                value={field.value}
                disabled={disabled}
                className="bg-muted/50 border-input grid grid-cols-3 gap-1 rounded-md border p-1"
                onValueChange={field.onChange}
              >
                {Object.values(A2vTemplateType).map((type) => (
                  <div className="relative" key={type}>
                    <RadioGroupItem
                      value={type}
                      id={`template-type-${type}`}
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor={`template-type-${type}`}
                      className="text-muted-foreground hover:text-foreground peer-data-[state=checked]:bg-card peer-data-[state=checked]:text-foreground flex h-9 cursor-pointer items-center justify-center rounded-sm px-2 text-center text-xs font-medium transition-colors peer-disabled:cursor-not-allowed peer-disabled:opacity-50"
                    >
                      {dashboardT(templateTypeLabelKey[type])}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            )}
          />
        </section>
        <section className="border-border space-y-3 border-t px-5 py-4">
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="network-template-logo" className="font-semibold">
              {dashboardT('a2v.networkTemplate.logo')}
            </Label>
            <Controller
              control={form.control}
              name="logoEnabled"
              render={({ field }) => (
                <Switch
                  id="network-template-logo"
                  checked={field.value}
                  disabled={disabled}
                  onCheckedChange={(checked) => {
                    field.onChange(checked);
                    if (checked) {
                      form.setValue(
                        'logoPosition',
                        nextAvailableOverlayPosition(
                          form.getValues('qrCodeEnabled')
                            ? form.getValues('qrCodePosition')
                            : undefined,
                          A2vOverlayPosition.TopLeft,
                        ),
                      );
                    }
                  }}
                />
              )}
            />
          </div>
          {configuration.logoEnabled ? (
            <>
              <button
                type="button"
                disabled={disabled}
                className="border-input bg-muted flex aspect-[16/7] w-full items-center justify-center overflow-hidden rounded-md border disabled:cursor-not-allowed disabled:opacity-60"
                onClick={onChooseLogo}
              >
                {configuration.logo?.url ? (
                  <img
                    className="h-full w-full object-contain"
                    src={configuration.logo.url}
                    alt={dashboardT('a2v.networkTemplate.logo')}
                  />
                ) : (
                  <span className="text-muted-foreground flex items-center gap-2 text-sm">
                    <ImageIcon className="h-4 w-4" aria-hidden="true" />
                    {dashboardT('a2v.networkTemplate.logoMissing')}
                  </span>
                )}
              </button>
              <Label>{dashboardT('a2v.networkTemplate.logoPosition')}</Label>
              <Controller
                control={form.control}
                name="logoPosition"
                render={({ field }) => (
                  <OverlayPositionPicker
                    value={field.value}
                    occupied={
                      configuration.qrCodeEnabled ? configuration.qrCodePosition : undefined
                    }
                    disabled={disabled}
                    onChange={field.onChange}
                  />
                )}
              />
              {form.formState.errors.logo ? (
                <p className="text-destructive text-xs" role="alert">
                  {form.formState.errors.logo.message}
                </p>
              ) : null}
            </>
          ) : null}
        </section>
        <section className="border-border space-y-3 border-t px-5 py-4">
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="network-template-qr" className="font-semibold">
              {dashboardT('a2v.networkTemplate.qrCode')}
            </Label>
            <Controller
              control={form.control}
              name="qrCodeEnabled"
              render={({ field }) => (
                <Switch
                  id="network-template-qr"
                  checked={field.value}
                  disabled={disabled}
                  onCheckedChange={(checked) => {
                    field.onChange(checked);
                    onQrEnabledChange(checked);
                    if (checked) {
                      form.setValue(
                        'qrCodePosition',
                        nextAvailableOverlayPosition(
                          form.getValues('logoEnabled')
                            ? form.getValues('logoPosition')
                            : undefined,
                          A2vOverlayPosition.BottomRight,
                        ),
                      );
                    }
                  }}
                />
              )}
            />
          </div>
          {configuration.qrCodeEnabled ? (
            <>
              <Label htmlFor="network-template-qr-url">
                {dashboardT('a2v.networkTemplate.qrUrl')}
              </Label>
              <div className="flex gap-2">
                <Input
                  id="network-template-qr-url"
                  aria-invalid={Boolean(form.formState.errors.qrCodeLink)}
                  disabled={disabled}
                  {...form.register('qrCodeLink', {
                    onChange: (event: ChangeEvent<HTMLInputElement>) => {
                      onQrValueChange(event.currentTarget.value);
                    },
                  })}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  disabled={
                    disabled || qrGenerating || !isValidHttpUrl(form.getValues('qrCodeLink'))
                  }
                  aria-label={dashboardT('a2v.networkTemplate.generateQr')}
                  onClick={() => {
                    onGenerateQr(form.getValues('qrCodeLink'));
                  }}
                >
                  <RefreshCw
                    className={qrGenerating ? 'h-4 w-4 animate-spin' : 'h-4 w-4'}
                    aria-hidden="true"
                  />
                </Button>
              </div>
              {form.formState.errors.qrCodeLink ? (
                <p className="text-destructive text-xs" role="alert">
                  {form.formState.errors.qrCodeLink.message}
                </p>
              ) : null}
              <Label>{dashboardT('a2v.networkTemplate.qrPosition')}</Label>
              <Controller
                control={form.control}
                name="qrCodePosition"
                render={({ field }) => (
                  <OverlayPositionPicker
                    value={field.value}
                    occupied={configuration.logoEnabled ? configuration.logoPosition : undefined}
                    disabled={disabled}
                    onChange={field.onChange}
                  />
                )}
              />
              {form.formState.errors.qrCodePosition ? (
                <p className="text-destructive text-xs" role="alert">
                  {form.formState.errors.qrCodePosition.message}
                </p>
              ) : null}
            </>
          ) : null}
        </section>
      </CardContent>
    </Card>
  );
}

const templateTypeLabelKey = {
  [A2vTemplateType.Image]: 'a2v.networkTemplate.types.image',
  [A2vTemplateType.Slideshow]: 'a2v.networkTemplate.types.slideshow',
  [A2vTemplateType.Video]: 'a2v.networkTemplate.types.video',
} as const;
